import React, { useEffect, useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import toast from "react-hot-toast";

import AddHubLink from "./AddHubLink";
import PlugPlay from "./PlugPlay";
import { GET_HUB_ITEMS, UPDATE_HUB_ORDER } from "../../api/hub";
import { UseIsAdmin, UseUserId } from "../../hooks/auth";
import { UseSelectedWorkspace } from "../../hooks/company";
import { Divider, useTheme } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SoftInput from "../SoftInput";
import HubSearchIcon from "./HubSearchIcon";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import SoftButton from "../SoftButton";
import HubLinkSuccessDialog from "./HubLinkSuccessDialog";
import { toDisplayText } from "../../helpers/errorText";
import { Skeleton } from "../shared/Loading";

const TILE_GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    md: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
  },
  gap: 3,
};

const mapHubItems = (list) =>
  list.map((plugin) => ({
    ...plugin,
    title: toDisplayText(plugin.title, "Link"),
    desc: toDisplayText(plugin.desc, ""),
  }));

const SortablePowerLink = ({
  plugin,
  userId,
  onRefetch,
  onEdit,
  disabled,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: plugin._id,
    disabled,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 2 : "auto",
  };

  return (
    <SoftBox
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        minWidth: 0,
      }}
    >
      <PlugPlay
        id={plugin._id}
        url={plugin.url}
        customImage={plugin.logo}
        name={toDisplayText(plugin.title, "Link")}
        fallbackImageUrl={plugin.type?.imageSrc}
        desc={plugin.desc}
        assignedTo={plugin.assignedTo}
        isOwner={plugin.userId === userId}
        onEnableChange={onRefetch}
        onEdit={onEdit}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragging={isDragging}
      />
    </SoftBox>
  );
};

const PowerLinks = () => {
  const workspace = UseSelectedWorkspace();
  const userId = UseUserId();
  const isAdmin = UseIsAdmin();
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlugin, setCurrentPlugin] = useState({});
  const [refetch, setRefetch] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [displayTitle, setDisplayTitle] = useState("");
  const [displayWeb, setDisplayWeb] = useState("");
  const [query, setQuery] = useState("");
  const [filteredPlugins, setFilteredPlugins] = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const theme = useTheme();
  const {
    primary: { main: primaryColor },
  } = theme.palette;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const openDialog = () => setDialog(true);
  const closeDialog = () => setDialog(false);
  const canReorder = !query;

  useEffect(() => {
    if (!workspace?._id) return;

    setLoading(true);
    GET_HUB_ITEMS(workspace._id, isAdmin ? null : userId)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setPlugins(mapHubItems(list));
      })
      .catch((error) => {
        console.error("Failed to load hub items:", error);
        setPlugins([]);
      })
      .finally(() => setLoading(false));
  }, [workspace, isAdmin, userId, refetch]);

  const getFilteredPlugins = (e) => {
    setQuery(e.target.value);
    setFilteredPlugins(
      plugins.filter((plugin) =>
        plugin.title.toLowerCase().includes(e.target.value.toLowerCase()),
      ),
    );
  };

  const handleEditPlugin = (plugin) => (event) => {
    event?.stopPropagation();
    setOpenModal(true);
    setEditMode(true);
    setCurrentPlugin(plugin);
  };

  const persistOrder = async (reorderedPlugins, previousPlugins) => {
    setIsSavingOrder(true);

    try {
      await UPDATE_HUB_ORDER(
        workspace._id,
        reorderedPlugins.map((plugin) => plugin._id),
      );
    } catch (error) {
      console.error("Failed to save hub link order:", error);
      setPlugins(previousPlugins);
      toast.error("Could not save link order. Please try again.");
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!canReorder || !over || active.id === over.id || !workspace?._id) {
      return;
    }

    const oldIndex = plugins.findIndex((plugin) => plugin._id === active.id);
    const newIndex = plugins.findIndex((plugin) => plugin._id === over.id);

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
      return;
    }

    const previousPlugins = plugins;
    const reorderedPlugins = arrayMove(plugins, oldIndex, newIndex);
    setPlugins(reorderedPlugins);
    await persistOrder(reorderedPlugins, previousPlugins);
  };

  const renderStaticTile = (plugin) => (
    <SoftBox key={plugin._id} sx={{ display: "flex", minWidth: 0 }}>
      <PlugPlay
        id={plugin._id}
        url={plugin.url}
        customImage={plugin.logo}
        name={toDisplayText(plugin.title, "Link")}
        fallbackImageUrl={plugin.type?.imageSrc}
        desc={plugin.desc}
        assignedTo={plugin.assignedTo}
        isOwner={plugin.userId === userId}
        onEnableChange={() => setRefetch(!refetch)}
        onEdit={handleEditPlugin(plugin)}
      />
    </SoftBox>
  );

  // showLabel=false renders a compact icon-only square (used in the mobile
  // search row); showLabel=true renders the full pill with text (desktop).
  const renderAddButton = (showLabel) => (
    <SoftButton
      variant="outlined"
      size="large"
      aria-label="Add a custom link"
      sx={{
        padding: showLabel ? "10px 25px" : 0,
        width: showLabel ? undefined : "44px",
        height: showLabel ? undefined : "44px",
        minWidth: showLabel ? undefined : "44px",
        minHeight: showLabel ? undefined : "44px",
        flexShrink: 0,
        textTransform: "none",
        borderColor: "#E9ECEF",
        borderRadius: "10px",
        color: "black !important",
        "&:hover": {
          borderColor: "#E9ECEF",
        },
        "&:focus:not(:hover)": {
          borderColor: "#E9ECEF",
        },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: showLabel ? 1 : 0,
      }}
      onClick={() => setOpenModal(true)}
    >
      <SoftBox
        width="24px"
        height="24px"
        bgColor="primary"
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderRadius="50%"
      >
        <AddRoundedIcon
          sx={{
            fill: "white",
          }}
        />
      </SoftBox>
      {showLabel && <SoftBox>Add a custom link</SoftBox>}
    </SoftButton>
  );

  const renderDraggableGrid = () => (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={plugins.map((plugin) => plugin._id)}
        strategy={rectSortingStrategy}
      >
        <SoftBox sx={TILE_GRID_SX}>
          {plugins.map((plugin) => (
            <SortablePowerLink
              key={plugin._id}
              plugin={plugin}
              userId={userId}
              onRefetch={() => setRefetch(!refetch)}
              onEdit={handleEditPlugin(plugin)}
              disabled={isSavingOrder}
            />
          ))}
        </SoftBox>
      </SortableContext>
    </DndContext>
  );

  const renderSkeletonGrid = () => (
    <SoftBox sx={TILE_GRID_SX}>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          variant="rect"
          height={96}
          radius={12}
        />
      ))}
    </SoftBox>
  );

  const renderStaticGrid = () => (
    <SoftBox sx={TILE_GRID_SX}>
      {(query ? filteredPlugins : plugins).map(renderStaticTile)}
    </SoftBox>
  );

  let linksContent;
  if (loading && plugins.length === 0) {
    linksContent = renderSkeletonGrid();
  } else if (canReorder && plugins.length > 0) {
    linksContent = renderDraggableGrid();
  } else {
    linksContent = renderStaticGrid();
  }

  return (
    <>
      <HubLinkSuccessDialog
        open={dialog}
        onClose={closeDialog}
        linkTitle={displayTitle}
        linkUrl={displayWeb}
      />
      <SoftBox bgColor="white" borderRadius="16px">
        <SoftBox
          padding={{
            mini: "1rem 1rem 0rem 1rem",
            md: "1.5rem 1.5rem 0rem 1.5rem",
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <SoftBox>
              <SoftTypography
                variant="h5"
                sx={{
                  color: "#343A40",
                  display: "block",
                }}
              >
                Power Links
              </SoftTypography>
              <SoftTypography
                variant="body2"
                sx={{
                  color: "#6C757D",
                  display: "block",
                  marginBottom: "0.5rem",
                }}
              >
                Customize your workspace by adding links to external tools and
                resources.
              </SoftTypography>
              {/* Mobile: search + add button share one row so the button
                  sits beside the input naturally instead of being nudged
                  into place with a guessed margin (which broke whenever the
                  drag hint above changed the stack's height). */}
              <SoftBox
                sx={{
                  display: { mini: "flex", lg: "none" },
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <SoftBox sx={{ flex: 1, minWidth: 0, "& > div": { width: "100%" } }}>
                  <SoftInput
                    placeholder="Search"
                    size="large"
                    icon={{
                      component: <HubSearchIcon />,
                      direction: "left",
                    }}
                    onChange={getFilteredPlugins}
                    sx={{
                      width: "100%",
                      "&.Mui-focused": {
                        borderColor: primaryColor,
                        boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                      },
                    }}
                  />
                </SoftBox>
                {renderAddButton(false)}
              </SoftBox>
            </SoftBox>
            <SoftBox
              display="flex"
              alignItems="center"
              gap="0.5rem"
              sx={{ display: { mini: "none", lg: "flex" } }}
            >
              <SoftBox
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  "& > div": {
                    width: "100%",
                  },
                }}
              >
                <SoftInput
                  placeholder="Search"
                  size="large"
                  icon={{
                    component: <HubSearchIcon />,
                    direction: "left",
                  }}
                  onChange={getFilteredPlugins}
                  sx={{
                    width: "100%",
                    "&.Mui-focused": {
                      borderColor: primaryColor,
                      boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                    },
                  }}
                />
              </SoftBox>
              {renderAddButton(true)}
            </SoftBox>
          </SoftBox>
          {openModal && (
            <AddHubLink
              isOpen={openModal}
              onClose={() => {
                setCurrentPlugin({});
                setOpenModal(false);
                setEditMode(false);
              }}
              openDialog={openDialog}
              isEdit={editMode}
              data={currentPlugin}
              refetch={() => setRefetch(!refetch)}
              setDisplayTitle={setDisplayTitle}
              setDisplayWeb={setDisplayWeb}
            />
          )}
        </SoftBox>

        <Divider
          sx={{
            backgroundColor: "#F2F4F7",
          }}
        />

        <SoftBox
          sx={{
            padding: {
              mini: "0.5rem 1rem 1rem 1rem",
              md: "0.75rem 1.5rem 1.5rem 1.5rem",
            },
          }}
        >
          {canReorder && plugins.length > 1 && (
            <SoftTypography
              variant="caption"
              sx={{
                color: "#98A2B3",
                display: "block",
                marginBottom: "0.75rem",
              }}
            >
              Drag the grip icon to arrange links in your preferred order.
            </SoftTypography>
          )}
          {linksContent}
        </SoftBox>
      </SoftBox>
    </>
  );
};

export default PowerLinks;
